import { PersistedUserEvent } from '@fruitful/types';
import { router } from 'expo-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEmpty, isNil } from 'lodash';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { isWeb } from 'smoothie';

import {
    isAuthenticatedPostSignup,
    isUserDeviceMfaVerified,
    storeUserToken,
} from '../../auth/helpers';
import { useStytchSession, useStytchUser } from '../../auth/hooks/useAuth';
import { stytchClient } from '../../auth/providers/AuthProvider';
import {
    isLoadingUserAfterBiometricsLoginAtom,
    isLoginInProgressAtom,
    isOtpSubmissionInProgressAtom,
    isUserSetupInProgressAtom,
} from '../../auth/state/AuthAtoms';
import { StorageKeys } from '../../common/constants';
import { properCase } from '../../common/misc';
import { useStorage } from '../../common/providers/StorageProvider';
import type { ContainerComponent } from '../../common/types/Common';
import { useLogUserEvent } from '../../events/hooks/useLogUserEvent';
import FetchError from '../../http/FetchError';
import { captureException, identifyUser } from '../../logger/Logger';
import { getPartialUser, getUser } from '../api/UserApi';
import { userFirstNameAtom, userLastNameAtom } from '../state/UserAtoms';
import type { User } from '../types/User';
import { getStoredVisitorId } from '../utils/visitorId';

type OptimisticUserDataUpdater = ((prevUser: User) => User | null) | null;

const initialContext = {
    user: null as User | null,
    loading: true,
    // True when Stytch auth succeeded but no Fruitful backend account exists
    // yet for this stytchId (e.g. abandoned signup between account creation
    // and finishing /sign-up/new-member/info).
    hasNoFruitfulUser: false,
    refetch: null as null | (() => Promise<void>),
    setOptimisticUserData: null as
        | null
        | ((updater: OptimisticUserDataUpdater) => void),
};
const UserContext = createContext<typeof initialContext>(initialContext);

export const useUser = () => {
    const context = useContext(UserContext);

    if (context == null) {
        throw new Error('useUser must be used within a UserProvider');
    }

    return context;
};

const UserProvider: ContainerComponent = ({ children }) => {
    const stytch = stytchClient;
    const partialUserRef = useRef<User | null>(null);
    const fullUserRef = useRef<User | null>(null);
    const isLoadingUserRef = useRef<boolean>(false);
    const [partialUser, setPartialUserState] = useState<User | null>(null);
    const [fullUser, setFullUserState] = useState<User | null>(null);
    const [isLoadingUser, setIsLoadingUserState] = useState(false);
    const [hasNoFruitfulUser, setHasNoFruitfulUser] = useState(false);
    const storedVisitorId = useMemo(() => getStoredVisitorId(), []);

    const setPartialUser = (user: User | null) => {
        partialUserRef.current = user;
        setPartialUserState(user);
    };
    const setFullUser = (user: User | null) => {
        fullUserRef.current = user;
        setFullUserState(user);
    };
    const setIsLoadingUser = (isLoading: boolean) => {
        isLoadingUserRef.current = isLoading;
        setIsLoadingUserState(isLoading);
    };

    const [refetch, setRefetch] = useState<null | (() => Promise<void>)>(null);

    const isLoadingUserAfterBiometricsLogin = useAtomValue(
        isLoadingUserAfterBiometricsLoginAtom,
    );
    const [isLoginInProgress, setIsLoginInProgressAtom] = useAtom(
        isLoginInProgressAtom,
    );
    const isOtpSubmissionInProgress = useAtomValue(
        isOtpSubmissionInProgressAtom,
    );
    const isUserSetupInProgress = useAtomValue(isUserSetupInProgressAtom);
    const setUserFirstNameAtom = useSetAtom(userFirstNameAtom);
    const setUserLastNameAtom = useSetAtom(userLastNameAtom);

    const storage = useStorage();
    const stytchUser = useStytchUser();
    const stytchSession = useStytchSession();

    const { logUserEvent } = useLogUserEvent();

    useEffect(() => {
        // Determine if the user has authed with biometrics or otp.
        const userHasAuthedWithBiometrics: boolean =
            stytchSession.session?.authentication_factors?.find(
                (authenticationFactor) =>
                    authenticationFactor.type === 'signature_challenge',
            ) !== undefined;
        const userHasAuthedWithOtp: boolean =
            stytchSession.session?.authentication_factors?.find(
                (authenticationFactor) => authenticationFactor.type === 'otp',
            ) !== undefined;
        const isDeviceMfaVerified = isUserDeviceMfaVerified(
            stytchUser.user?.user_id,
        );

        if (stytchSession.session?.user_id === undefined) {
            // If the stytchSession is null, reset all fields, as it likely means the user is no longer authenticated.
            // Drop the current Sprout thread pointer so the next login starts a fresh chat.
            storage.removeItem(StorageKeys.SPROUT_THREAD_ID);
            storage.removeItem(StorageKeys.SPROUT_STREAM_OPTIONS);
            setFullUser(null);
            setPartialUser(null);
            setIsLoadingUser(false);
            setHasNoFruitfulUser(false);
            return;
        }

        if (stytchUser?.user?.user_id === undefined) {
            if (
                isLoginInProgress &&
                userHasAuthedWithBiometrics &&
                isLoadingUserAfterBiometricsLogin === false
            ) {
                // If we land in this case via biometric auth attempts, we take the user to the email sign in page to use as a workaround.
                // This workaround was originally necessary due to a bug in the Stytch SDK where biometric auth stopped returning the user.
                // It can be removed if this code is no longer serving us or actively causing an issue, but it is nice to have as a safeguard.
                captureException(
                    new Error(
                        'Unexpected auth scenario: Stytch session is valid after biometric authentication, but there is no Stytch user',
                    ),
                    {
                        userId: stytchSession.session.user_id,
                    },
                );
                router.push('/sign-in/email');
            }

            // Stytch user is not loaded yet
            return;
        }

        if (
            isOtpSubmissionInProgress === true ||
            isUserSetupInProgress === true
        ) {
            return;
        }

        // Fetches a partial user by stytchId and populates fields based off of the stytchUser and stytchSession.
        const fetchPartialUser = async () => {
            if (stytchUser.user?.user_id == null || isLoadingUserRef.current) {
                return;
            }

            if (partialUserRef.current) {
                return partialUserRef.current;
            }

            try {
                setIsLoadingUser(true);

                const fetchedPartialUser = await getPartialUser({
                    stytchId: stytchUser.user?.user_id,
                });

                if (isNil(fetchedPartialUser) || isEmpty(fetchedPartialUser)) {
                    throw new Error('partial user not found');
                }

                setHasNoFruitfulUser(false);
                fetchedPartialUser.isPartial = true;
                fetchedPartialUser.visitorId = storedVisitorId ?? undefined;

                const userEmailIsVerified = stytchUser.user.emails.find(
                    (email) => email.verified,
                );
                fetchedPartialUser.isEmailVerified =
                    userEmailIsVerified !== undefined;
                fetchedPartialUser.isPhoneOnStytch =
                    stytchUser.user.phone_numbers.length > 0;
                fetchedPartialUser.isFullyAuthed =
                    userHasAuthedWithOtp || userHasAuthedWithBiometrics;

                setUserFirstNameAtom(
                    properCase(fetchedPartialUser.firstName ?? ''),
                );
                setUserLastNameAtom(
                    properCase(fetchedPartialUser.lastName ?? ''),
                );
                setPartialUser(fetchedPartialUser);
                identifyUser(fetchedPartialUser);

                storeUserToken(stytch.session.getTokens()?.session_jwt);

                return fetchedPartialUser;
            } catch (error) {
                // A 403 here means the Stytch auth succeeded but there is no
                // Fruitful backend account for this stytchId yet (e.g. they
                // created a Stytch account but never finished "Tell us more
                // about you"). That's an expected state, not a real error.
                if (error instanceof FetchError && error.status === 403) {
                    setHasNoFruitfulUser(true);
                    return;
                }

                captureException(new Error(`Error fetching partial user`), {
                    error,
                    stytchId: stytchUser.user?.user_id,
                });
                return;
            } finally {
                setIsLoadingUser(false);
            }
        };

        // Fetches a full user by stytchId and populates fields based off of the stytchUser and stytchSession.
        const fetchFullUser = async (isRefetch = false) => {
            if (stytchUser.user?.user_id == null || isLoadingUserRef.current) {
                return;
            }

            if (!isRefetch && fullUserRef.current) {
                return fullUserRef.current;
            }

            try {
                !isRefetch && setIsLoadingUser(true);

                const user = await getUser({
                    stytchId: stytchUser.user?.user_id,
                });

                if (isNil(user) || isEmpty(user)) {
                    throw new Error('full user not found');
                }

                user.isPartial = false;
                user.visitorId = storedVisitorId ?? undefined;

                const userEmailIsVerified = stytchUser.user.emails.find(
                    (email) => email.verified,
                );
                user.isEmailVerified = userEmailIsVerified !== undefined;
                user.isPhoneOnStytch = stytchUser.user.phone_numbers.length > 0;
                user.isUsingBiometrics = userHasAuthedWithBiometrics;
                user.isFullyAuthed =
                    userHasAuthedWithBiometrics ||
                    userHasAuthedWithOtp ||
                    isAuthenticatedPostSignup(user) ||
                    isDeviceMfaVerified ||
                    user.disableMfa === true;
                user.firstName = properCase(user.firstName ?? '');
                user.lastName = properCase(user.lastName ?? '');

                setUserFirstNameAtom(user.firstName);
                setUserLastNameAtom(user.lastName);
                const memberInfo = {
                    firstName: user.firstName,
                };
                storage.setString(
                    StorageKeys.MEMBER_INFO,
                    JSON.stringify(memberInfo),
                );

                setFullUser(user);
                identifyUser(user);

                // Log and update session started_at to local storage
                const sessionId = stytchSession.session?.session_id;
                const storageSessionId = storage.getString(
                    StorageKeys.LATEST_SESSION_ID,
                );

                if (
                    sessionId != null &&
                    (storageSessionId == null || sessionId !== storageSessionId)
                ) {
                    // Store data in local storage before logging to reduce chance of duplicate events being logged from re-renders or multiple tabs
                    storage.setString(StorageKeys.LATEST_SESSION_ID, sessionId);

                    const eventType = isWeb
                        ? PersistedUserEvent.WEB_APP_LOGIN
                        : PersistedUserEvent.MOBILE_APP_LOGIN;
                    logUserEvent(eventType, {
                        isWeb,
                        isEmailVerified: user.isEmailVerified,
                        isPhoneOnStytch: user.isPhoneOnStytch,
                        isUsingBiometrics: user.isUsingBiometrics,
                        userHasAuthedWithOtp,
                        isAuthenticatedPostSignup:
                            isAuthenticatedPostSignup(user),
                        disableMfa: user.disableMfa,
                        sessionId,
                        sessionStartedAt: stytchSession.session?.started_at,
                    });
                }

                setIsLoginInProgressAtom(false);
                return user;
            } catch (error) {
                captureException(new Error('Error fetching full user'), {
                    error,
                    stytchId: stytchUser.user?.user_id,
                });
                return;
            } finally {
                !isRefetch && setIsLoadingUser(false);
            }
        };

        const fetchUser = async () => {
            // Determine if the user has disabled MFA, by checking the value on a partial user.
            const _partialUser = await fetchPartialUser();
            const userHasDisabledMfa = _partialUser?.disableMfa ?? false;
            const userIsAuthedPostSignup =
                isAuthenticatedPostSignup(_partialUser);

            // Fetch full user if they have:
            // 1) Auth'ed with biometrics
            // 2) Auth'ed with OTP
            // 3) Disabled MFA
            // 4) Just performed a password reset
            // 5) Just signed up and is authed
            // 6) A device that has been remembered as MFA verified
            if (
                userHasAuthedWithBiometrics ||
                userHasAuthedWithOtp ||
                userHasDisabledMfa ||
                isUserDeviceMfaVerified(stytchUser.user?.user_id) ||
                userIsAuthedPostSignup
            ) {
                await fetchFullUser();
            }
        };

        // IMPORTANT: This has to be wrapped in an extra function so that we properly set a function (instead of a promise) on local state.
        setRefetch(() => async () => {
            await fetchFullUser(true);
        });

        // Fetch the user, either Partial or Full.
        fetchUser().catch((e) => {
            captureException(new Error('Error fetching user', e as object));
        });

        // The stytchSession object is updated asynchronously, notably the `last_accessed_at` field, so we want to be selective on which fields we use to trigger the hook.
        // user_id is used as a proxy for detecting a null state change.
        // authentication_factors is updated after the user submits either: 1) an otp code on the otp login page, 2) a biometric prompt on the biometrics login page.
    }, [
        stytchSession.session?.user_id,
        stytchSession.session?.session_id,
        JSON.stringify(stytchSession.session?.authentication_factors),
        stytchUser.user?.user_id,
        isLoadingUserAfterBiometricsLogin === false,
        isOtpSubmissionInProgress,
        isUserSetupInProgress,
    ]);

    /**
     * Use with caution: Updates user data optimistically without waiting for server confirmation.
     * This is useful for immediate UI updates when you expect the server to eventually
     * reflect the same changes.
     *
     * @param userData - Partial user data to merge with the current user data
     * @returns void
     */
    const setOptimisticUserData = useCallback(
        (updater: OptimisticUserDataUpdater) => {
            if (fullUser == null || updater == null) {
                return;
            }
            setFullUserState((prevFullUser) => {
                if (prevFullUser == null) {
                    return prevFullUser;
                }

                const nextFullUser = updater(prevFullUser);
                if (nextFullUser == null) {
                    return prevFullUser;
                }

                fullUserRef.current = nextFullUser;
                return nextFullUser;
            });
        },
        [fullUser],
    );

    return (
        <UserContext.Provider
            value={{
                user: fullUser ?? partialUser,
                loading: isLoadingUser,
                hasNoFruitfulUser,
                refetch,
                setOptimisticUserData,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export default UserProvider;
